import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { startLeagueRegisterOAuth, registerLeague, startManualLeagueOAuth } from '../services/api';
import '../styles/fonts.css';
import '../styles/organizer.css';

const REGISTER_ERRORS = {
    falha_token: 'Falha ao autenticar com iRacing. Tente novamente.',
    falha_userinfo: 'Não foi possível identificar sua conta iRacing. Tente novamente.',
    liga_nao_encontrada: 'Liga não encontrada no iRacing. Verifique o ID informado.',
    nao_e_dono: 'Sua conta iRacing não tem permissão para cadastrar esta liga. Apenas o owner ou admins definidos no iRacing podem fazê-lo.',
    falha_iracing: 'Erro ao consultar o iRacing. Tente novamente.',
};

function AddLeague() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    const registerToken = searchParams.get('register_token') || '';
    const verifiedLeagueId = searchParams.get('league_id') || '';
    const verifiedDisplayName = searchParams.get('display_name') || '';
    const registerError = searchParams.get('register_error') || '';

    // Step 1 state
    const [leagueId, setLeagueId] = useState(verifiedLeagueId || '');
    const [oauthLoading, setOauthLoading] = useState(false);
    const [oauthError, setOauthError] = useState(
        registerError ? (REGISTER_ERRORS[registerError] || 'Erro na verificação. Tente novamente.') : null
    );

    // Step 2 state
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [discordUrl, setDiscordUrl] = useState('');
    const [whatsappUrl, setWhatsappUrl] = useState('');
    const [instagramUrl, setInstagramUrl] = useState('');
    const [twitchUrl, setTwitchUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [manualLoading, setManualLoading] = useState(false);

    async function handleManual() {
        setManualLoading(true);
        try {
            const data = await startManualLeagueOAuth();
            window.location.href = data.oauth_url;
        } catch {
            setManualLoading(false);
            setOauthError('Não foi possível iniciar a verificação. Tente novamente.');
        }
    }

    async function handleVerify(e) {
        e.preventDefault();
        if (!leagueId || isNaN(Number(leagueId))) {
            setOauthError('ID da liga inválido.');
            return;
        }
        setOauthLoading(true);
        setOauthError(null);
        try {
            const data = await startLeagueRegisterOAuth(leagueId);
            window.location.href = data.oauth_url;
        } catch (err) {
            setOauthLoading(false);
            const msg = err?.error || '';
            if (msg.includes('já está cadastrada')) setOauthError(msg);
            else setOauthError('Não foi possível iniciar a verificação. Tente novamente.');
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        if (password.length < 6) { setError(t('addLeague.passwordTooShort')); return; }
        if (password !== confirmPassword) { setError(t('addLeague.passwordMismatch')); return; }
        setLoading(true);
        try {
            const result = await registerLeague({
                register_token: registerToken,
                password,
                youtube_url: youtubeUrl,
                discord_url: discordUrl,
                whatsapp_url: whatsappUrl,
                instagram_url: instagramUrl,
                twitch_url: twitchUrl,
            });
            navigate(`/leagues/${result.league_id}`);
        } catch (err) {
            setError(err?.error || t('addLeague.genericError'));
        } finally {
            setLoading(false);
        }
    }

    // Step 2: verified, showing password form
    if (registerToken) {
        return (
            <div style={{ minHeight: '100vh', background: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
                <Header />
                <main style={{ flex: 1, maxWidth: '600px', margin: '0 auto', padding: '48px 24px', width: '100%' }}>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: 6 }}>
                        {t('addLeague.title')}
                    </h1>
                    <div style={{ background: '#1e2a1e', border: '1px solid #3a5a3a', borderRadius: 8, padding: '14px 18px', marginBottom: 24 }}>
                        <span style={{ color: '#4caf50', fontWeight: 600, fontSize: '0.9rem' }}>
                            ✓ {t('addLeague.verifiedAs')}: <strong>{verifiedDisplayName}</strong>
                        </span>
                        <div style={{ color: '#9ba3ab', fontSize: '0.8rem', marginTop: 4 }}>
                            Liga ID: {verifiedLeagueId}
                        </div>
                    </div>

                    {error && (
                        <div style={{ background: '#3d2020', color: '#e07070', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem' }}>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="organizer-section-title">{t('addLeague.passwordSection')}</div>

                        <label className="organizer-label">{t('addLeague.password')} *</label>
                        <input className="organizer-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required />

                        <label className="organizer-label">{t('addLeague.confirmPassword')} *</label>
                        <input className="organizer-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />

                        <div className="organizer-section-title" style={{ marginTop: 24 }}>{t('addLeague.socialSection')}</div>

                        <label className="organizer-label">YouTube</label>
                        <input className="organizer-input" type="url" placeholder="https://youtube.com/@sualiga" value={youtubeUrl} onChange={e => setYoutubeUrl(e.target.value)} />

                        <label className="organizer-label">Discord</label>
                        <input className="organizer-input" type="url" placeholder="https://discord.gg/..." value={discordUrl} onChange={e => setDiscordUrl(e.target.value)} />

                        <label className="organizer-label">WhatsApp</label>
                        <input className="organizer-input" type="url" placeholder="https://chat.whatsapp.com/..." value={whatsappUrl} onChange={e => setWhatsappUrl(e.target.value)} />

                        <label className="organizer-label">Instagram</label>
                        <input className="organizer-input" type="url" placeholder="https://instagram.com/sualiga" value={instagramUrl} onChange={e => setInstagramUrl(e.target.value)} />

                        <label className="organizer-label">Twitch</label>
                        <input className="organizer-input" type="url" placeholder="https://twitch.tv/sualiga" value={twitchUrl} onChange={e => setTwitchUrl(e.target.value)} />

                        <button className="organizer-btn organizer-btn-primary" type="submit" disabled={loading} style={{ width: '100%', marginTop: 24 }}>
                            {loading ? t('common.loading') : t('addLeague.submit')}
                        </button>
                    </form>
                </main>
                <Footer />
            </div>
        );
    }

    const IRACING_COLOR = '#e3b341';
    const MANUAL_COLOR = '#58a6ff';

    // Step 1: two cards side by side
    return (
        <div style={{ minHeight: '100vh', background: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
            <Header />
            <main style={{ flex: 1, maxWidth: '860px', margin: '0 auto', padding: '48px 24px', width: '100%' }}>
                <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#fff', marginBottom: 8 }}>
                    {t('addLeague.title')}
                </h1>
                <p style={{ fontSize: '13px', color: '#8b949e', marginBottom: 40 }}>
                    {t('addLeague.subtitle2')}
                </p>

                {oauthError && (
                    <div style={{ background: '#3d2020', color: '#e07070', borderRadius: 6, padding: '12px 16px', marginBottom: 24, fontSize: '0.85rem' }}>
                        {oauthError}
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>

                    {/* Card A — iRacing League */}
                    <div
                        style={{ background: '#21262d', border: '1px solid #30363d', borderRadius: 10, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 12, height: '100%', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = IRACING_COLOR}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#30363d'}
                    >
                        <i className="fas fa-flag-checkered" style={{ fontSize: 26, color: IRACING_COLOR }} />
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#e6edf3', marginBottom: 6 }}>
                                {t('addLeague.cardIracingTitle')}
                            </div>
                            <div style={{ fontSize: 13, color: '#8b949e', lineHeight: 1.6 }}>
                                {t('addLeague.cardIracingDesc')}
                            </div>
                        </div>

                        <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                            <label style={{ fontSize: '0.8rem', color: '#8b949e' }}>{t('addLeague.leagueIdLabel')}</label>
                            <input
                                type="number"
                                placeholder={t('addLeague.leagueIdPlaceholder')}
                                value={leagueId}
                                onChange={e => setLeagueId(e.target.value)}
                                required
                                style={{
                                    width: '100%', boxSizing: 'border-box',
                                    background: '#161b22', border: '1px solid #30363d', borderRadius: 6,
                                    color: '#e6edf3', fontSize: '1rem', padding: '10px 14px',
                                    outline: 'none', appearance: 'textfield',
                                }}
                            />
                            <p style={{ fontSize: '0.75rem', color: '#9ba3ab', margin: '-4px 0 4px' }}>
                                {t('addLeague.leagueIdHint')}
                            </p>
                            <button
                                type="submit"
                                disabled={oauthLoading}
                                style={{ background: IRACING_COLOR, color: '#0d1117', border: 'none', borderRadius: 6, padding: '11px 0', fontWeight: 700, fontSize: '0.9rem', cursor: oauthLoading ? 'not-allowed' : 'pointer', opacity: oauthLoading ? 0.7 : 1 }}
                            >
                                {oauthLoading ? t('common.loading') : t('addLeague.verifyBtn')}
                            </button>
                        </form>

                        <div style={{ marginTop: 'auto', paddingTop: 12, fontSize: '11px', color: '#8b949e', lineHeight: 1.5 }}>
                            🔒 {t('addLeague.securityNote')}
                        </div>
                    </div>

                    {/* Card B — Manual */}
                    <div
                        style={{ background: '#21262d', border: '1px solid #30363d', borderRadius: 10, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 12, height: '100%', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = MANUAL_COLOR}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#30363d'}
                    >
                        <i className="fas fa-pencil-alt" style={{ fontSize: 26, color: MANUAL_COLOR }} />
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#e6edf3', marginBottom: 6 }}>
                                {t('addLeague.manualSection')}
                            </div>
                            <div style={{ fontSize: 13, color: '#8b949e', lineHeight: 1.6 }}>
                                {t('addLeague.manualDesc')}
                            </div>
                        </div>

                        <div style={{ marginTop: 'auto' }}>
                            <button
                                type="button"
                                disabled={manualLoading}
                                onClick={handleManual}
                                style={{ width: '100%', background: 'transparent', color: MANUAL_COLOR, border: `1px solid ${MANUAL_COLOR}`, borderRadius: 6, padding: '10px 0', fontWeight: 700, fontSize: '0.9rem', cursor: manualLoading ? 'not-allowed' : 'pointer', opacity: manualLoading ? 0.7 : 1, marginTop: 8 }}
                            >
                                {manualLoading ? t('common.loading') : t('addLeague.manualBtn')}
                            </button>
                        </div>
                    </div>

                </div>
            </main>
            <Footer />
        </div>
    );
}

export default AddLeague;
